import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../config/api';
import toast from 'react-hot-toast';
import * as ps from '../services/pocketsmith.service';
import { tw, monoFont, groupColors, getCategoryStyle } from '../utils/terminalTheme';

interface Property {
  id: number;
  name: string;
  address: string;
}

type SortKey = 'date' | 'payee' | 'amount' | 'account' | 'category' | '';

const fmt = (n: number) => '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const PocketSmith: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<any>(null);
  const [transactions, setTransactions] = useState<ps.PocketSmithTransaction[]>([]);
  const [summary, setSummary] = useState<ps.SummaryResponse | null>(null);
  const [properties, setProperties] = useState<Property[]>([]);
  const [accounts, setAccounts] = useState<ps.GroupedAccountsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [payeeFilter, setPayeeFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [accountFilter, setAccountFilter] = useState(searchParams.get('account') || '');
  const [groupFilter, setGroupFilter] = useState(searchParams.get('group') || '');
  const [unmatchedOnly, setUnmatchedOnly] = useState(false);
  const [hideTransfers, setHideTransfers] = useState(false);

  // Sorting
  const [sortBy, setSortBy] = useState<SortKey>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Bulk select
  const [selectedTxns, setSelectedTxns] = useState<Set<number>>(new Set());
  const [bulkPropertyId, setBulkPropertyId] = useState<number>(0);

  // Expand row
  const [expandedTxn, setExpandedTxn] = useState<number | null>(null);

  // Analytics
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsTab, setAnalyticsTab] = useState<'category' | 'monthly' | 'recurring'>('category');
  const [categoryData, setCategoryData] = useState<ps.CategoryBreakdown[]>([]);
  const [monthlyData, setMonthlyData] = useState<ps.MonthlyTrend[]>([]);
  const [recurringData, setRecurringData] = useState<ps.RecurringPattern[]>([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Smart suggestions
  const [suggestions, setSuggestions] = useState<Map<string, ps.MatchSuggestion>>(new Map());

  // Duplicates
  const [duplicateCount, setDuplicateCount] = useState(0);

  // Ledger mode
  const isLedgerMode = !!searchParams.get('account');
  const ledgerAccountName = searchParams.get('account') || '';

  // Sync filter state when URL params change
  useEffect(() => {
    const urlGroup = searchParams.get('group') || '';
    const urlAccount = searchParams.get('account') || '';
    if (urlGroup !== groupFilter) setGroupFilter(urlGroup);
    if (urlAccount !== accountFilter) setAccountFilter(urlAccount);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refs to hold latest filter values (avoids useCallback churn)
  const filtersRef = useRef({ startDate, endDate, payeeFilter, searchFilter, accountFilter, groupFilter, unmatchedOnly, hideTransfers, sortBy, sortDir });
  filtersRef.current = { startDate, endDate, payeeFilter, searchFilter, accountFilter, groupFilter, unmatchedOnly, hideTransfers, sortBy, sortDir };

  const fetchTransactions = useCallback(async (p: number = 1) => {
    const f = filtersRef.current;
    try {
      const result = await ps.getTransactions({
        start_date: f.startDate || undefined,
        end_date: f.endDate || undefined,
        payee: f.payeeFilter || undefined,
        search: f.searchFilter || undefined,
        account_name: f.accountFilter || undefined,
        account_group: f.groupFilter || undefined,
        unmatched_only: f.unmatchedOnly,
        exclude_transfers: f.hideTransfers,
        sort_by: f.sortBy || undefined,
        sort_dir: f.sortDir,
        page: p,
        per_page: 50,
      });
      setTransactions(result.data);
      setTotalPages(result.pages);
      setTotal(result.total);
      setPage(p);
      setSelectedTxns(new Set());
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
    }
  }, []); // stable reference — reads filters from ref

  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const [catRes, monthRes, recRes] = await Promise.all([
        ps.getCategoryBreakdown({
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          account_group: groupFilter || undefined,
          exclude_transfers: true,
        }),
        ps.getMonthlyTrend({
          account_group: groupFilter || undefined,
          exclude_transfers: true,
        }),
        ps.getRecurringPatterns({
          account_group: groupFilter || undefined,
        }),
      ]);
      setCategoryData(catRes.data);
      setMonthlyData(monthRes.data);
      setRecurringData(recRes.data);
    } catch (err) {
      console.error('Analytics fetch error:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [startDate, endDate, groupFilter]);

  // Init: runs once on mount — loads status, summary, properties, accounts, suggestions, duplicates
  const initRef = useRef(false);
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    const init = async () => {
      setLoading(true);
      try {
        const [statusRes, summaryRes, propsRes, acctRes, suggestRes, dupRes] = await Promise.all([
          ps.checkStatus().catch(() => ({ success: false, error: 'API key not configured' })),
          ps.getSummary().catch(() => null),
          api.get('/properties/').catch(() => ({ data: [] })),
          ps.getGroupedAccounts().catch(() => null),
          ps.getMatchSuggestions().catch(() => ({ data: [] })),
          ps.getDuplicates().catch(() => ({ duplicate_groups: 0 })),
        ]);
        setStatus(statusRes);
        if (summaryRes) setSummary(summaryRes);
        setProperties((propsRes.data || []).sort((a: any, b: any) => (a.address || a.name || '').localeCompare(b.address || b.name || '')));
        if (acctRes) setAccounts(acctRes);
        // Build suggestions map (payee_lower → suggestion)
        if (suggestRes?.data) {
          const map = new Map<string, ps.MatchSuggestion>();
          suggestRes.data.forEach((s: ps.MatchSuggestion) => map.set(s.payee, s));
          setSuggestions(map);
        }
        setDuplicateCount(dupRes?.duplicate_groups || 0);
        await fetchTransactions(1);
      } catch (err) {
        console.error('Init error:', err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [fetchTransactions]);

  // Filter changes: debounced fetch (400ms for text, immediate for toggles/selects)
  const filterGenRef = useRef(0);
  useEffect(() => {
    filterGenRef.current += 1;
    const gen = filterGenRef.current;
    const delay = setTimeout(() => {
      if (gen === filterGenRef.current && initRef.current) {
        fetchTransactions(1);
      }
    }, 400);
    return () => clearTimeout(delay);
  }, [searchFilter, payeeFilter, startDate, endDate, accountFilter, groupFilter, unmatchedOnly, hideTransfers, sortBy, sortDir, fetchTransactions]);

  // Fetch analytics when panel opens or filters change
  useEffect(() => {
    if (showAnalytics) fetchAnalytics();
  }, [showAnalytics, fetchAnalytics]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await ps.syncTransactions({
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      });
      let msg = `Synced ${result.synced} transactions (${result.created} new, ${result.updated} updated)`;
      if (result.rules_applied && result.rules_applied > 0) {
        msg += ` — ${result.rules_applied} auto-matched by rules`;
      }
      toast.success(msg);
      await fetchTransactions(1);
      const summaryRes = await ps.getSummary().catch(() => null);
      if (summaryRes) setSummary(summaryRes);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleMatch = async (txnId: number, propertyId: number) => {
    try {
      await ps.matchTransaction(txnId, { property_id: propertyId || 0 });
      toast.success('Transaction matched');
      await fetchTransactions(page);
    } catch (err) {
      toast.error('Failed to match transaction');
    }
  };

  const handleBulkMatch = async () => {
    if (selectedTxns.size === 0 || !bulkPropertyId) return;
    try {
      const result = await ps.bulkMatchTransactions(Array.from(selectedTxns), bulkPropertyId);
      toast.success(`Matched ${result.updated} transactions`);
      setSelectedTxns(new Set());
      setBulkPropertyId(0);
      await fetchTransactions(page);
    } catch (err) {
      toast.error('Bulk match failed');
    }
  };

  const handleCreateRuleFromTxn = async (txn: ps.PocketSmithTransaction) => {
    try {
      const result = await ps.createRuleFromTransaction(txn.id);
      if (result.already_existed) {
        toast.success('Rule already exists for this payee');
      } else {
        toast.success(`Rule created: "${result.payee_contains}" → ${getPropertyName(result.property_id)}`);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to create rule');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await api.get('/pocketsmith/transactions/export', {
        params: {
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          account_name: accountFilter || undefined,
          account_group: groupFilter || undefined,
          search: searchFilter || undefined,
          unmatched_only: unmatchedOnly || undefined,
          exclude_transfers: hideTransfers || undefined,
        },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactions_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const getPropertyName = (propertyId: number) => {
    const prop = properties.find(p => p.id === propertyId);
    return prop ? (prop.address || prop.name) : `Property #${propertyId}`;
  };

  const handleGroupFilterChange = (newGroup: string) => {
    setGroupFilter(newGroup);
    setAccountFilter('');
    const params = new URLSearchParams();
    if (newGroup) params.set('group', newGroup);
    setSearchParams(params);
  };

  const clearLedgerMode = () => {
    setAccountFilter('');
    setGroupFilter('');
    setSearchParams({});
  };

  const handleSort = (col: SortKey) => {
    if (sortBy === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir(col === 'date' ? 'desc' : 'asc');
    }
  };

  const sortIndicator = (col: SortKey) => {
    if (sortBy !== col) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  const toggleSelect = (id: number) => {
    setSelectedTxns(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedTxns.size === transactions.length) {
      setSelectedTxns(new Set());
    } else {
      setSelectedTxns(new Set(transactions.map(t => t.id)));
    }
  };

  if (loading) {
    return (
      <div className={tw.loadingContainer}>
        <div className={tw.loading}></div>
      </div>
    );
  }

  const connected = status?.success === true;

  // Get group badge style for active filter
  const activeGroupStyle = groupFilter ? getCategoryStyle(groupFilter, groupColors) : null;

  // Analytics helpers
  const maxCatAmount = categoryData.length > 0 ? Math.max(...categoryData.map(c => Math.abs(c.total))) : 1;
  const maxMonthAmount = monthlyData.length > 0 ? Math.max(...monthlyData.map(m => Math.max(m.income, Math.abs(m.expenses)))) : 1;

  return (
    <div className={tw.pageWide}>
      {/* Terminal Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white" style={monoFont}>
            <span className="text-cyan-400">~/</span>
            {isLedgerMode ? ledgerAccountName.toLowerCase().replace(/\s+/g, '-') : 'pocketsmith'}
          </h1>
          <p className="text-gray-500 text-sm font-mono mt-2">
            {isLedgerMode ? (
              <span>
                <span className="text-green-500">$</span> ledger --account=&quot;{ledgerAccountName}&quot;{' '}
                <span
                  onClick={clearLedgerMode}
                  className="text-cyan-400 cursor-pointer hover:underline"
                >
                  [back to all]
                </span>
              </span>
            ) : groupFilter ? (
              <span>
                <span className="text-green-500">$</span> filter --group=
                <span className={activeGroupStyle?.text || 'text-cyan-400'}>{groupFilter}</span>
                {' '}
                <span
                  onClick={clearLedgerMode}
                  className="text-cyan-400 cursor-pointer hover:underline"
                >
                  [show all]
                </span>
              </span>
            ) : (
              <span>
                <span className="text-green-500">$</span> sync &amp; match pocketsmith transactions to properties
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-3">
          <Link
            to="/pocketsmith/rules"
            className={tw.btnSecondary + ' no-underline'}
          >
            Manage Rules
          </Link>
          <button
            onClick={handleSync}
            disabled={!connected || syncing}
            className={`${connected ? tw.btnPrimary : 'bg-gray-700 text-gray-500 px-4 py-2 rounded-md font-mono text-sm cursor-not-allowed'} ${syncing ? 'opacity-60' : ''}`}
          >
            {syncing ? 'Syncing...' : 'Sync Transactions'}
          </button>
        </div>
      </div>

      {/* Status + Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className={tw.statCard}>
          <div className={tw.statLabel}>Connection</div>
          <div className={`text-xl font-bold font-mono ${connected ? 'text-green-400' : 'text-red-400'}`}>
            {connected ? 'Connected' : 'Not Connected'}
          </div>
          {connected && status?.data?.name && (
            <div className="text-gray-600 text-xs font-mono mt-1">{status.data.name}</div>
          )}
        </div>
        <div className={tw.statCard}>
          <div className={tw.statLabel}>Total Synced</div>
          <div className={tw.statValueCyan}>{summary?.total_transactions || 0}</div>
        </div>
        <div className={tw.statCard}>
          <div className={tw.statLabel}>Matched</div>
          <div className={`${tw.statValue} text-green-400`}>{summary?.matched || 0}</div>
        </div>
        <div className={tw.statCard}>
          <div className={tw.statLabel}>Unmatched</div>
          <div className={`${tw.statValue} text-amber-400`}>{summary?.unmatched || 0}</div>
        </div>
      </div>

      {/* Group Filter Tabs + Analytics Toggle */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-2">
          <button
            onClick={() => handleGroupFilterChange('')}
            className={!groupFilter ? tw.filterActive : tw.filterInactive}
          >
            All Groups
          </button>
          {(['PERSONAL', 'BUSINESS', 'INVESTMENT'] as const).map(g => {
            const gStyle = getCategoryStyle(g, groupColors);
            const isActive = groupFilter === g;
            return (
              <button
                key={g}
                onClick={() => handleGroupFilterChange(groupFilter === g ? '' : g)}
                className={`px-3 py-1.5 rounded-md text-xs font-mono font-semibold transition-all border-l-[3px] ${
                  isActive
                    ? `${gStyle.bg} ${gStyle.text} border ${gStyle.border} border-l-current`
                    : 'bg-gray-800 text-gray-400 hover:text-white border border-gray-700 border-l-transparent'
                }`}
              >
                {g.charAt(0) + g.slice(1).toLowerCase()}
              </button>
            );
          })}
        </div>
        <button
          onClick={() => setShowAnalytics(!showAnalytics)}
          className={showAnalytics ? tw.filterActive : tw.filterInactive}
        >
          {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
        </button>
      </div>

      {/* Analytics Panel */}
      {showAnalytics && (
        <div className="bg-gray-800/80 border border-gray-700/50 rounded-lg p-5 mb-6">
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setAnalyticsTab('category')}
              className={analyticsTab === 'category' ? tw.filterActive : tw.filterInactive}
            >
              By Category
            </button>
            <button
              onClick={() => setAnalyticsTab('monthly')}
              className={analyticsTab === 'monthly' ? tw.filterActive : tw.filterInactive}
            >
              Monthly Trend
            </button>
            <button
              onClick={() => setAnalyticsTab('recurring')}
              className={analyticsTab === 'recurring' ? tw.filterActive : tw.filterInactive}
            >
              Recurring ({recurringData.length})
            </button>
          </div>

          {analyticsLoading ? (
            <div className={tw.empty}>Loading analytics...</div>
          ) : analyticsTab === 'category' ? (
            <div className="max-h-[400px] overflow-y-auto">
              {categoryData.length === 0 ? (
                <div className={tw.empty}>No category data available</div>
              ) : categoryData.map((cat, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-700/30">
                  <div
                    className="w-[180px] flex-shrink-0 text-gray-300 text-sm font-mono overflow-hidden text-ellipsis whitespace-nowrap"
                    title={cat.category}
                  >
                    {cat.category}
                  </div>
                  <div className="flex-1 relative h-5 bg-gray-900 rounded overflow-hidden">
                    <div
                      className={`absolute top-0 left-0 h-full rounded opacity-70 ${cat.total >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                      style={{ width: `${Math.min((Math.abs(cat.total) / maxCatAmount) * 100, 100)}%` }}
                    />
                  </div>
                  <div className={`w-[100px] text-right font-mono text-sm flex-shrink-0 ${cat.total >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {cat.total >= 0 ? '+' : '-'}{fmt(cat.total)}
                  </div>
                  <div className="w-[40px] text-right text-gray-600 text-xs font-mono flex-shrink-0">
                    {cat.count}
                  </div>
                </div>
              ))}
            </div>
          ) : analyticsTab === 'monthly' ? (
            <div className="max-h-[400px] overflow-y-auto">
              {monthlyData.length === 0 ? (
                <div className={tw.empty}>No monthly data available</div>
              ) : monthlyData.map((m, i) => (
                <div key={i} className="py-2.5 border-b border-gray-700/30">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-[70px] flex-shrink-0 text-gray-300 text-sm font-mono font-semibold">{m.month}</div>
                    <div className="flex-1">
                      {/* Income bar */}
                      <div className="flex items-center gap-2 mb-0.5">
                        <div className="w-3 text-[10px] text-gray-600 flex-shrink-0 font-mono">+</div>
                        <div className="flex-1 h-3 bg-gray-900 rounded overflow-hidden">
                          <div
                            className="h-full bg-green-500 rounded opacity-80"
                            style={{ width: `${maxMonthAmount > 0 ? (m.income / maxMonthAmount) * 100 : 0}%` }}
                          />
                        </div>
                        <div className="w-[80px] text-right font-mono text-xs text-green-400 flex-shrink-0">{fmt(m.income)}</div>
                      </div>
                      {/* Expenses bar */}
                      <div className="flex items-center gap-2">
                        <div className="w-3 text-[10px] text-gray-600 flex-shrink-0 font-mono">-</div>
                        <div className="flex-1 h-3 bg-gray-900 rounded overflow-hidden">
                          <div
                            className="h-full bg-red-500 rounded opacity-80"
                            style={{ width: `${maxMonthAmount > 0 ? (Math.abs(m.expenses) / maxMonthAmount) * 100 : 0}%` }}
                          />
                        </div>
                        <div className="w-[80px] text-right font-mono text-xs text-red-400 flex-shrink-0">{fmt(m.expenses)}</div>
                      </div>
                    </div>
                    <div className={`w-[90px] text-right font-mono text-sm font-semibold flex-shrink-0 ${m.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {m.net >= 0 ? '+' : '-'}{fmt(m.net)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : analyticsTab === 'recurring' ? (
            <div className="max-h-[400px] overflow-y-auto">
              {recurringData.length === 0 ? (
                <div className={tw.empty}>No recurring patterns detected</div>
              ) : (
                <table className="w-full border-collapse text-sm font-mono">
                  <thead>
                    <tr>
                      <th className={tw.tableHeader}>Payee</th>
                      <th className={tw.tableHeader}>Frequency</th>
                      <th className={tw.tableHeaderRight}>Avg Amount</th>
                      <th className="px-5 py-3 text-center text-xs font-mono text-gray-500 uppercase tracking-wider">Count</th>
                      <th className={tw.tableHeader}>Last</th>
                      <th className={tw.tableHeader}>Next Expected</th>
                    </tr>
                  </thead>
                  <tbody className={tw.tableDivider}>
                    {recurringData.map((r, i) => (
                      <tr key={i} className={tw.tableRow}>
                        <td className="px-5 py-2.5 text-gray-300 font-mono text-sm">{r.payee}</td>
                        <td className="px-5 py-2.5">
                          <span className="bg-blue-500/15 text-blue-400 border border-blue-500/30 px-2 py-0.5 rounded text-xs font-mono">
                            {r.frequency}
                          </span>
                        </td>
                        <td className={`px-5 py-2.5 text-right font-mono text-sm ${r.avg_amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {r.avg_amount >= 0 ? '+' : '-'}{fmt(r.avg_amount)}
                        </td>
                        <td className="px-5 py-2.5 text-center text-gray-500 font-mono text-sm">{r.transaction_count}</td>
                        <td className="px-5 py-2.5 text-gray-500 font-mono text-sm">{r.last_date}</td>
                        <td className="px-5 py-2.5 font-mono text-sm">
                          <span className={r.is_overdue ? 'text-red-400' : 'text-gray-500'}>
                            {r.next_expected}
                            {r.is_overdue && <span className="ml-1.5 text-xs text-red-400">(overdue)</span>}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* Duplicates Warning */}
      {duplicateCount > 0 && (
        <div className="bg-amber-500/10 rounded-lg px-5 py-3 mb-4 border border-amber-500/30 flex items-center gap-3">
          <span className="text-amber-400 text-lg">&#9888;</span>
          <span className="text-amber-300 text-sm font-mono">
            {duplicateCount} potential duplicate group{duplicateCount !== 1 ? 's' : ''} detected (same date + payee + amount)
          </span>
        </div>
      )}

      {/* Filters */}
      <div className="bg-gray-800/80 border border-gray-700/50 rounded-lg px-5 py-4 mb-6 flex flex-wrap gap-4 items-end">
        <div>
          <label className={tw.label}>start_date</label>
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className={tw.input}
          />
        </div>
        <div>
          <label className={tw.label}>end_date</label>
          <input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className={tw.input}
          />
        </div>
        <div>
          <label className={tw.label}>search</label>
          <input
            type="text"
            value={searchFilter}
            onChange={e => setSearchFilter(e.target.value)}
            placeholder="Payee, category, notes..."
            className={`${tw.input} min-w-[180px]`}
          />
        </div>
        <div>
          <label className={tw.label}>account</label>
          <select
            value={accountFilter}
            onChange={e => setAccountFilter(e.target.value)}
            className={`${tw.select} min-w-[180px]`}
          >
            <option value="">All Accounts</option>
            {accounts?.groups.map(group => (
              <optgroup key={group.key} label={group.label}>
                {group.accounts.map(acct => (
                  <option key={acct.id} value={acct.name}>{acct.name}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-1.5 text-gray-500 text-xs font-mono pb-1 cursor-pointer">
          <input
            type="checkbox"
            checked={unmatchedOnly}
            onChange={e => setUnmatchedOnly(e.target.checked)}
            className="cursor-pointer accent-cyan-500"
          />
          unmatched_only
        </label>
        <label className="flex items-center gap-1.5 text-gray-500 text-xs font-mono pb-1 cursor-pointer">
          <input
            type="checkbox"
            checked={hideTransfers}
            onChange={e => setHideTransfers(e.target.checked)}
            className="cursor-pointer accent-cyan-500"
          />
          hide_transfers
        </label>
        <button onClick={() => fetchTransactions(1)} className={tw.btnPrimary}>
          Filter
        </button>
        <button
          onClick={handleExportCSV}
          className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 px-4 py-2 rounded-md font-mono text-sm transition-colors hover:bg-emerald-500/25"
        >
          Export CSV
        </button>
      </div>

      {/* Transactions Table */}
      <div className={tw.tableWrap}>
        <div className="px-5 py-4 border-b border-gray-700/50 flex justify-between items-center">
          <h2 className="text-white text-lg font-mono font-semibold m-0">
            Transactions ({total})
            {groupFilter && activeGroupStyle && (
              <span className={`${activeGroupStyle.text} text-sm font-normal ml-2`}>-- {groupFilter}</span>
            )}
          </h2>
          {summary?.date_range?.start && (
            <span className="text-gray-600 text-xs font-mono">{summary.date_range.start} to {summary.date_range.end}</span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-900/50">
                <th className="px-4 py-3 text-center text-xs font-mono text-gray-500 uppercase tracking-wider w-10">
                  <input
                    type="checkbox"
                    checked={transactions.length > 0 && selectedTxns.size === transactions.length}
                    onChange={toggleSelectAll}
                    className="cursor-pointer accent-cyan-500"
                  />
                </th>
                <th className={tw.tableHeader + ' cursor-pointer select-none whitespace-nowrap'} onClick={() => handleSort('date')}>
                  Date{sortIndicator('date')}
                </th>
                <th className={tw.tableHeader + ' cursor-pointer select-none whitespace-nowrap'} onClick={() => handleSort('payee')}>
                  Payee{sortIndicator('payee')}
                </th>
                <th className={tw.tableHeader + ' cursor-pointer select-none whitespace-nowrap'} onClick={() => handleSort('category')}>
                  Category{sortIndicator('category')}
                </th>
                <th className={tw.tableHeaderRight + ' cursor-pointer select-none whitespace-nowrap'} onClick={() => handleSort('amount')}>
                  Amount{sortIndicator('amount')}
                </th>
                {!isLedgerMode && (
                  <th className={tw.tableHeader + ' cursor-pointer select-none whitespace-nowrap'} onClick={() => handleSort('account')}>
                    Account{sortIndicator('account')}
                  </th>
                )}
                {isLedgerMode && (
                  <th className={tw.tableHeaderRight}>Balance</th>
                )}
                <th className={tw.tableHeader}>Property</th>
                <th className="px-5 py-3 text-center text-xs font-mono text-gray-500 uppercase tracking-wider">Rule</th>
              </tr>
            </thead>
            <tbody className={tw.tableDivider}>
              {transactions.map(txn => (
                <React.Fragment key={txn.id}>
                  <tr
                    className={`cursor-pointer transition-colors ${
                      expandedTxn === txn.id
                        ? 'bg-gray-800/60'
                        : selectedTxns.has(txn.id)
                        ? 'bg-cyan-500/5'
                        : 'hover:bg-gray-700/20'
                    }`}
                    onClick={(e) => {
                      const tag = (e.target as HTMLElement).tagName;
                      if (tag === 'SELECT' || tag === 'OPTION' || tag === 'BUTTON' || tag === 'INPUT') return;
                      setExpandedTxn(expandedTxn === txn.id ? null : txn.id);
                    }}
                  >
                    <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedTxns.has(txn.id)}
                        onChange={() => toggleSelect(txn.id)}
                        className="cursor-pointer accent-cyan-500"
                      />
                    </td>
                    <td className="px-5 py-3.5 text-gray-400 text-sm font-mono whitespace-nowrap">{txn.date}</td>
                    <td className="px-5 py-3.5 text-white text-sm font-mono">
                      {txn.payee}
                      {txn.is_transfer && (
                        <span className="ml-2 bg-blue-500/15 text-blue-400 border border-blue-500/30 px-1.5 py-0.5 rounded text-xs font-mono">
                          transfer
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-sm font-mono">
                      <span className={txn.category_override ? "text-cyan-400" : "text-gray-500"}>{txn.category_name}</span>
                    </td>
                    <td className={`px-5 py-3.5 text-right font-mono text-sm whitespace-nowrap ${txn.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {txn.amount >= 0 ? '+' : '-'}{fmt(txn.amount)}
                    </td>
                    {!isLedgerMode && (
                      <td className="px-5 py-3.5 text-gray-500 text-sm font-mono">{txn.account_name}</td>
                    )}
                    {isLedgerMode && (
                      <td className={`px-5 py-3.5 text-right font-mono text-sm ${txn.closing_balance != null && txn.closing_balance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {txn.closing_balance != null ? fmt(txn.closing_balance) : '---'}
                      </td>
                    )}
                    <td className="px-5 py-3.5" onClick={e => e.stopPropagation()}>
                      <select
                        value={txn.property_id || ''}
                        onChange={e => handleMatch(txn.id, parseInt(e.target.value) || 0)}
                        className={`${tw.select} min-w-[160px]`}
                      >
                        <option value="">--</option>
                        {properties.map(p => (
                          <option key={p.id} value={p.id}>{p.address || p.name}</option>
                        ))}
                      </select>
                      {!txn.property_id && suggestions.has(txn.payee) && (
                        <div className="text-xs font-mono mt-1">
                          <span
                            onClick={() => handleMatch(txn.id, suggestions.get(txn.payee)!.suggested_property_id)}
                            className="text-cyan-400 cursor-pointer hover:underline"
                          >
                            Suggest: {getPropertyName(suggestions.get(txn.payee)!.suggested_property_id)}
                          </span>
                          <span className="text-gray-600 ml-1">
                            ({suggestions.get(txn.payee)!.unmatched_count}x)
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-center" onClick={e => e.stopPropagation()}>
                      {txn.property_id ? (
                        <button
                          onClick={() => handleCreateRuleFromTxn(txn)}
                          title={`Create rule: "${txn.payee}" → ${getPropertyName(txn.property_id)}`}
                          className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 px-2.5 py-1 rounded text-xs font-mono whitespace-nowrap cursor-pointer hover:bg-emerald-500/25 transition-colors"
                        >
                          Create Rule
                        </button>
                      ) : (
                        <span className="text-gray-700 font-mono">---</span>
                      )}
                    </td>
                  </tr>

                  {/* Expanded Detail Row */}
                  {expandedTxn === txn.id && (
                    <tr className="bg-gray-900/50">
                      <td colSpan={isLedgerMode ? 8 : 8} className="px-4 pb-4 pl-14">
                        <div className="grid grid-cols-3 gap-4 p-4 bg-gray-800/80 border border-gray-700/50 rounded-lg">
                          <div>
                            <div className={tw.statLabel}>Original Payee</div>
                            <div className="text-gray-300 text-sm font-mono">{txn.original_payee || '---'}</div>
                          </div>
                          <div>
                            <div className={tw.statLabel}>Account</div>
                            <div className="text-gray-300 text-sm font-mono">{txn.account_name}</div>
                          </div>
                          <div>
                            <div className={tw.statLabel}>Status</div>
                            <div className="text-gray-300 text-sm font-mono">{txn.status || '---'}</div>
                          </div>
                          <div>
                            <div className={tw.statLabel}>Note</div>
                            <div className="text-gray-300 text-sm font-mono">{txn.note || '---'}</div>
                          </div>
                          <div>
                            <div className={tw.statLabel}>Labels</div>
                            <div className="text-gray-300 text-sm font-mono">{txn.labels || '---'}</div>
                          </div>
                          <div>
                            <div className={tw.statLabel}>Closing Balance</div>
                            <div className="text-gray-300 text-sm font-mono">
                              {txn.closing_balance != null ? fmt(txn.closing_balance) : '---'}
                            </div>
                          </div>
                          <div>
                            <div className={tw.statLabel}>PocketSmith ID</div>
                            <div className="text-gray-300 text-sm font-mono">{txn.pocketsmith_id}</div>
                          </div>
                          <div>
                            <div className={tw.statLabel}>Transfer</div>
                            <div className={`text-sm font-mono ${txn.is_transfer ? 'text-blue-400' : 'text-gray-600'}`}>
                              {txn.is_transfer ? 'Yes' : 'No'}
                            </div>
                          </div>
                          <div>
                            <div className={tw.statLabel}>Synced At</div>
                            <div className="text-gray-300 text-sm font-mono">{txn.created_at ? new Date(txn.created_at).toLocaleString() : '---'}</div>
                          </div>
                        </div>
                        {/* User Notes */}
                        <div className="mt-3 p-3 bg-gray-900/50 border border-gray-700/30 rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <div className={tw.statLabel + ' mb-0'}>User Notes</div>
                          </div>
                          <textarea
                            className="w-full bg-gray-900 text-gray-300 rounded px-3 py-2 border border-gray-700 focus:border-cyan-500 focus:outline-none font-mono text-sm resize-none"
                            rows={2}
                            placeholder="Add notes about this transaction..."
                            defaultValue={txn.user_notes || ''}
                            onBlur={async (e) => {
                              const val = e.target.value;
                              if (val !== (txn.user_notes || '')) {
                                try {
                                  await api.patch(`/pocketsmith/transactions/${txn.id}/annotate`, { user_notes: val });
                                  txn.user_notes = val;
                                  toast.success('Note saved');
                                } catch { toast.error('Failed to save note'); }
                              }
                            }}
                          />
                        </div>
                        {/* Quick Actions */}
                        <div className="flex gap-2 mt-2.5">
                          <button
                            onClick={() => { navigator.clipboard.writeText(txn.payee); toast.success('Payee copied'); }}
                            className={tw.btnSecondary}
                          >
                            Copy Payee
                          </button>
                          {txn.original_payee && txn.original_payee !== txn.payee && (
                            <button
                              onClick={() => { navigator.clipboard.writeText(txn.original_payee); toast.success('Original payee copied'); }}
                              className={tw.btnSecondary}
                            >
                              Copy Original Payee
                            </button>
                          )}
                          {txn.property_id && (
                            <button
                              onClick={() => handleCreateRuleFromTxn(txn)}
                              className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 px-4 py-2 rounded-md font-mono text-sm cursor-pointer hover:bg-emerald-500/25 transition-colors"
                            >
                              Create Auto-Match Rule
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {transactions.length === 0 && (
                <tr>
                  <td colSpan={8} className={tw.empty}>
                    {connected ? 'No transactions found for the current filters.' : 'Connect PocketSmith API key to get started.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={tw.tableFooter}>
            <button
              onClick={() => fetchTransactions(page - 1)}
              disabled={page <= 1}
              className={`${tw.btnSecondary} ${page <= 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Previous
            </button>
            <span className="text-gray-500 text-sm font-mono">Page {page} of {totalPages}</span>
            <button
              onClick={() => fetchTransactions(page + 1)}
              disabled={page >= totalPages}
              className={`${tw.btnSecondary} ${page >= totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Bulk Match Bar */}
      {selectedTxns.size > 0 && (
        <div className="fixed bottom-0 left-[250px] right-0 bg-gray-800/95 border-t-2 border-cyan-500 px-8 py-4 flex items-center gap-4 z-50 backdrop-blur-sm">
          <span className="text-white text-sm font-mono font-semibold">
            {selectedTxns.size} selected
          </span>
          <span className="text-gray-500 text-xs font-mono">match_to:</span>
          <select
            value={bulkPropertyId}
            onChange={e => setBulkPropertyId(parseInt(e.target.value) || 0)}
            className={`${tw.select} min-w-[200px]`}
          >
            <option value={0}>Select property...</option>
            {properties.map(p => (
              <option key={p.id} value={p.id}>{p.address || p.name}</option>
            ))}
          </select>
          <button
            onClick={handleBulkMatch}
            disabled={!bulkPropertyId}
            className={bulkPropertyId ? tw.btnPrimary + ' font-semibold' : 'bg-gray-700 text-gray-500 px-4 py-2 rounded-md font-mono text-sm cursor-not-allowed'}
          >
            Match All
          </button>
          <button
            onClick={() => { setSelectedTxns(new Set()); setBulkPropertyId(0); }}
            className={tw.filterInactive}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Last sync info */}
      {summary?.last_sync && (
        <p className="text-gray-600 text-xs font-mono mt-4 text-right">
          Last sync: {new Date(summary.last_sync).toLocaleString()}
        </p>
      )}
    </div>
  );
};

export default PocketSmith;
